<!-- eslint-disable vue/v-slot-style -->
<template>
  <div class="bg-white dark:bg-slate-900">
    <div
      v-if="hasInboxPermission('Inbox can assign')"
      class="multiselect-wrap--small"
    >
      <contact-details-item
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template v-slot:button>
          <woot-button
            v-if="showSelfAssign"
            icon="arrow-right"
            variant="link"
            size="small"
            @click="onSelfAssign"
          >
            {{ $t('CONVERSATION_SIDEBAR.SELF_ASSIGN') }}
          </woot-button>
        </template>
      </contact-details-item>
      <multiselect-dropdown
        :options="allowedAgentList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @click="onClickAssignAgent"
      />
    </div>
    <div
      v-if="hasInboxPermission('Inbox assign to group')"
      class="multiselect-wrap--small"
    >
      <contact-details-item
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <multiselect-dropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.INPUT')
        "
        @click="onClickAssignTeam"
      />
    </div>
    <div
      v-if="hasInboxPermission('Inbox priority of conversations')"
      class="multiselect-wrap--small"
    >
      <contact-details-item
        compact
        :title="$t('CONVERSATION.PRIORITY.TITLE')"
      />
      <multiselect-dropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @click="onClickAssignPriority"
      />
    </div>
    <contact-details-item
      v-if="hasInboxPermission('Inbox assign labels')"
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <conversation-labels
      v-if="hasInboxPermission('Inbox assign labels')"
      :conversation-id="conversationId"
    />

    <!-- QM §5.6.3 routing actions — owner surface -->
    <div v-if="showQueueSection" class="px-2 py-2">
      <contact-details-item
        compact
        :title="$t('QUEUES_MGMT.CONVERSATION.TITLE')"
      />
      <!-- QM §5.6.1 — SLA bar: informational, so it shows to any viewer of a live ticket -->
      <queue-sla-bar :routing-state="routingState" />
      <template v-if="showQueueActions">
        <div
          v-if="isLocked"
          class="flex items-center gap-1 mb-2 text-sm text-slate-700 dark:text-slate-200"
        >
          <fluent-icon icon="lock-closed" size="14" />
          {{ $t(lockBadgeKey) }}
        </div>
        <div class="flex items-center gap-2">
          <woot-button
            size="small"
            variant="smooth"
            color-scheme="secondary"
            icon="lock-closed"
            :is-loading="isLockUpdating"
            @click="toggleLock"
          >
            {{
              isLocked
                ? $t('QUEUES_MGMT.CONVERSATION.UNLOCK')
                : $t('QUEUES_MGMT.CONVERSATION.LOCK')
            }}
          </woot-button>
          <woot-button
            size="small"
            variant="smooth"
            color-scheme="warning"
            icon="arrow-reply"
            :is-loading="isReturning"
            @click="onReturnToQueue"
          >
            {{ $t('QUEUES_MGMT.CONVERSATION.RETURN') }}
          </woot-button>
        </div>
      </template>
    </div>

    <woot-confirm-modal
      ref="returnConfirm"
      :title="$t('QUEUES_MGMT.CONVERSATION.RETURN_CONFIRM.TITLE')"
      :description="$t('QUEUES_MGMT.CONVERSATION.RETURN_CONFIRM.DESC')"
      :confirm-label="$t('QUEUES_MGMT.CONVERSATION.RETURN_CONFIRM.YES')"
      :cancel-label="$t('QUEUES_MGMT.CONVERSATION.RETURN_CONFIRM.NO')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import QueueSlaBar from './QueueSlaBar.vue';
import agentMixin from 'dashboard/mixins/agentMixin';
import teamMixin from 'dashboard/mixins/conversation/teamMixin';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import QueueActionsAPI from 'dashboard/api/queueActions';
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    QueueSlaBar,
  },
  mixins: [
    agentMixin,
    alertMixin,
    teamMixin,
    hasPermissionMixin,
    queueSupervisionMixin,
  ],
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
      ],
      isLockUpdating: false,
      isReturning: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    routingState() {
      return this.currentChat?.routing_state;
    },
    queueManagementEnabled() {
      return this.$store.getters['accounts/isFeatureEnabledonAccount'](
        this.currentAccountId,
        FEATURE_FLAGS.QUEUE_MANAGEMENT
      );
    },
    isLocked() {
      return !!this.routingState?.locked;
    },
    isConversationOwner() {
      return this.currentChat?.meta?.assignee?.id === this.currentUser.id;
    },
    // QM §3.2, §4.13 — the lock pins the ticket to its OWNER.
    lockBadgeKey() {
      return this.isConversationOwner
        ? 'QUEUES_MGMT.CONVERSATION.LOCKED_BADGE'
        : 'QUEUES_MGMT.CONVERSATION.LOCKED_BY_OTHER';
    },
    // QM §5.6.1 — the section itself only needs a queue-managed, still-live ticket: the
    // SLA bar is read-only, unlike the actions below it.
    showQueueSection() {
      return (
        this.queueManagementEnabled &&
        !!this.routingState &&
        this.routingState.state !== 'closed'
      );
    },
    // QM §5.6.3 Owner surface plus the supervisor surface (§4.13.1 unlock, §5.5.4
    // return on the owner's behalf) — the same pair the backend authorises in
    // QueueActionsController. Both actions need an owner, so a ticket still
    // waiting in its queue has nothing to act on.
    showQueueActions() {
      if (!this.queueManagementEnabled || !this.routingState) {
        return false;
      }
      if (this.routingState.state !== 'assigned') {
        return false;
      }
      return this.isConversationOwner || this.hasQueueSupervision;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            this.showAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          })
          .catch(error => {
            this.showAlert(
              error?.response?.data?.error ||
                this.$t('CONVERSATION.CHANGE_AGENT_FAILED')
            );
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            this.showAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            this.$track(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            this.showAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
    allowedAgentList() {
      return this.agentsList.filter(
        agent =>
          agent.see_all_conversations ||
          agent.id === 0 ||
          agent.assignable_to_inbox?.includes(this.inboxId)
      );
    },
  },
  methods: {
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },

    // Lock/unlock — store updates via the CONVERSATION_ROUTING_UPDATED broadcast.
    async toggleLock() {
      this.isLockUpdating = true;
      const wasLocked = this.isLocked;
      try {
        if (wasLocked) {
          await QueueActionsAPI.unlock(this.conversationId);
        } else {
          await QueueActionsAPI.lock(this.conversationId);
        }
        this.showAlert(
          this.$t(
            wasLocked
              ? 'QUEUES_MGMT.CONVERSATION.TOAST.UNLOCKED'
              : 'QUEUES_MGMT.CONVERSATION.TOAST.LOCKED'
          )
        );
      } catch (error) {
        this.showAlert(
          error?.response?.data?.error ||
            this.$t('QUEUES_MGMT.CONVERSATION.TOAST.ERROR')
        );
      } finally {
        this.isLockUpdating = false;
      }
    },
    async onReturnToQueue() {
      const ok = await this.$refs.returnConfirm.showConfirmation();
      if (ok) this.returnToQueue();
    },
    async returnToQueue() {
      this.isReturning = true;
      try {
        await QueueActionsAPI.returnToQueue(this.conversationId);
        this.showAlert(this.$t('QUEUES_MGMT.CONVERSATION.TOAST.RETURNED'));
      } catch (error) {
        this.showAlert(
          error?.response?.data?.error ||
            this.$t('QUEUES_MGMT.CONVERSATION.TOAST.ERROR')
        );
      } finally {
        this.isReturning = false;
      }
    },
  },
};
</script>
